<script setup>
import {
    NCard, NSpace, NH1, NH2, NH3, NP, NAlert,
    NDivider, NList, NListItem, NGradientText
} from 'naive-ui';
import { useI18n } from 'vue-i18n';
import { 
    FileText, AlertTriangle, Shield, Users, 
    Ban, Scale, CreditCard, Bell 
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

const lastUpdated = "January 1, 2025";
</script>

<template>
    <div class="terms-container">
        <!-- Hero Section -->
        <div class="hero-section">
            <NH1 class="hero-title">
                <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
                    {{ dt('terms.heroTitle') }}
                </NGradientText>
            </NH1>
            <NP class="hero-subtitle">{{ dt('terms.heroSubtitle') }}</NP>
            <NP class="last-updated">{{ dt('terms.lastUpdated') }}: {{ lastUpdated }}</NP>
        </div>

        <!-- Critical Disclaimer -->
        <NAlert type="error" class="critical-alert">
            <template #icon>
                <AlertTriangle />
            </template>
            <strong>{{ dt('terms.critical.title') }}</strong>
            <NP style="margin-top: 8px;">{{ dt('terms.critical.content') }}</NP>
        </NAlert>

        <!-- Acceptance of Terms -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #36d1dc, #5b86e5);">
                    <FileText size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.acceptance.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.acceptance.p1') }}</NP>
            <NP class="section-text">{{ dt('terms.acceptance.p2') }}</NP>
            <NP class="section-text">{{ dt('terms.acceptance.p3') }}</NP>
        </NCard>

        <!-- Platform Description -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #5b86e5, #ef473a);">
                    <Users size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.platform.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.platform.p1') }}</NP>
            <NP class="section-text">{{ dt('terms.platform.p2') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 5" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.platform.features.${i}`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- NO LEGAL ADVICE - Critical Section -->
        <NCard class="content-card disclaimer-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #ef473a, #f7b733);">
                    <AlertTriangle size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.noLegalAdvice.title') }}</NH2>
            </div>
            <NAlert type="error" style="margin-bottom: 16px;">
                {{ dt('terms.noLegalAdvice.alert') }}
            </NAlert>
            <NP class="section-text"><strong>{{ dt('terms.noLegalAdvice.p1') }}</strong></NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 6" :key="i">
                    <template #prefix>
                        <Ban size="16" color="#ef473a" />
                    </template>
                    {{ dt(`terms.noLegalAdvice.items.${i}`) }}
                </NListItem>
            </NList>
            <NP class="section-text highlight-text">{{ dt('terms.noLegalAdvice.p2') }}</NP>
        </NCard>

        <!-- Limitation of Liability -->
        <NCard class="content-card disclaimer-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #f7b733, #a8edea);">
                    <Shield size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.liability.title') }}</NH2>
            </div>
            <NP class="section-text"><strong>{{ dt('terms.liability.p1') }}</strong></NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 8" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.liability.items.${i}`) }}
                </NListItem>
            </NList>
            <NP class="section-text highlight-text">{{ dt('terms.liability.p2') }}</NP>
            <NP class="section-text highlight-text">{{ dt('terms.liability.p3') }}</NP>
        </NCard>

        <!-- User Responsibilities -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #a8edea, #36d1dc);">
                    <Users size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.userResponsibilities.title') }}</NH2>
            </div>
            <NH3 class="subsection-title">{{ dt('terms.userResponsibilities.clients.title') }}</NH3>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 5" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.userResponsibilities.clients.items.${i}`) }}
                </NListItem>
            </NList>

            <NH3 class="subsection-title">{{ dt('terms.userResponsibilities.practitioners.title') }}</NH3>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 7" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.userResponsibilities.practitioners.items.${i}`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Verification Process -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #36d1dc, #5b86e5);">
                    <Scale size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.verification.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.verification.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 4" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.verification.items.${i}`) }}
                </NListItem>
            </NList>
            <NAlert type="warning" style="margin-top: 16px;">
                {{ dt('terms.verification.disclaimer') }}
            </NAlert>
        </NCard>

        <!-- Reviews and Ratings -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('terms.reviews.title') }}</NH2>
            <NP class="section-text">{{ dt('terms.reviews.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 6" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.reviews.items.${i}`) }}
                </NListItem>
            </NList>
            <NAlert type="warning" style="margin-top: 16px;">
                {{ dt('terms.reviews.disclaimer') }}
            </NAlert>
        </NCard>

        <!-- Payments and Fees -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #5b86e5, #ef473a);">
                    <CreditCard size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.payments.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.payments.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 5" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.payments.items.${i}`) }}
                </NListItem>
            </NList>
            <NAlert type="warning" style="margin-top: 16px;">
                {{ dt('terms.payments.disclaimer') }}
            </NAlert>
        </NCard>

        <!-- Prohibited Conduct -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #ef473a, #f7b733);">
                    <Ban size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.prohibited.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.prohibited.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 10" :key="i">
                    <template #prefix>
                        <Ban size="16" color="#ef473a" />
                    </template>
                    {{ dt(`terms.prohibited.items.${i}`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Intellectual Property -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('terms.ip.title') }}</NH2>
            <NP class="section-text">{{ dt('terms.ip.p1') }}</NP>
            <NP class="section-text">{{ dt('terms.ip.p2') }}</NP>
        </NCard>

        <!-- Termination -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('terms.termination.title') }}</NH2>
            <NP class="section-text">{{ dt('terms.termination.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 4" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`terms.termination.items.${i}`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Dispute Resolution -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('terms.disputes.title') }}</NH2>
            <NP class="section-text">{{ dt('terms.disputes.p1') }}</NP>
            <NP class="section-text">{{ dt('terms.disputes.p2') }}</NP>
            <NP class="section-text">{{ dt('terms.disputes.p3') }}</NP>
        </NCard>

        <!-- Changes to Terms -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #f7b733, #a8edea);">
                    <Bell size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('terms.changes.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('terms.changes.p1') }}</NP>
            <NP class="section-text">{{ dt('terms.changes.p2') }}</NP>
        </NCard>

        <!-- Contact -->
        <NCard class="content-card contact-card">
            <NH2 class="section-title">{{ dt('terms.contact.title') }}</NH2>
            <NP class="section-text">{{ dt('terms.contact.content') }}</NP>
            <div class="contact-info">
                <NP><strong>{{ dt('terms.contact.email') }}:</strong> legal@lawdosti.com</NP>
                <NP><strong>{{ dt('terms.contact.address') }}:</strong> {{ dt('terms.contact.addressValue') }}</NP>
            </div>
        </NCard>

        <!-- Final Acknowledgment -->
        <NAlert type="info" class="final-alert">
            <strong>{{ dt('terms.finalAck.title') }}</strong>
            <NP style="margin-top: 8px;">{{ dt('terms.finalAck.content') }}</NP>
        </NAlert>
    </div>
</template>

<style scoped>
.terms-container {
    padding: 16px;
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    color: #333;
    overflow-x: hidden;
    box-sizing: border-box;
}

.hero-section {
    text-align: center;
    padding: 40px 0;
}

.hero-title {
    margin-bottom: 12px;
}

.hero-subtitle {
    font-size: 1.2rem;
    color: #555;
    margin-bottom: 8px;
}

.last-updated {
    font-size: 0.9rem;
    color: #888;
    font-style: italic;
}

.critical-alert,
.final-alert {
    margin: 30px 0;
    border-radius: 12px;
}

.content-card {
    margin: 24px 0;
    border-radius: 16px;
}

.disclaimer-card {
    border: 2px solid #ef473a;
    background: linear-gradient(135deg, rgba(239, 71, 58, 0.02), rgba(247, 183, 51, 0.02));
}

.section-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.section-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.section-title {
    margin-bottom: 16px;
    color: #333;
}

.subsection-title {
    margin: 24px 0 12px 0;
    color: #444;
}

.section-text {
    margin-bottom: 16px;
    line-height: 1.8;
    color: #555;
    font-size: 1rem;
}

.highlight-text {
    font-weight: 600;
    color: #ef473a;
    padding: 12px;
    background: rgba(239, 71, 58, 0.05);
    border-radius: 8px;
    border-left: 4px solid #ef473a;
}

.section-list {
    margin-top: 16px;
}

.list-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.contact-card {
    background: linear-gradient(135deg, rgba(54, 209, 220, 0.05), rgba(91, 134, 229, 0.05));
}

.contact-info {
    margin-top: 20px;
    padding: 20px;
    background: white;
    border-radius: 12px;
    border-left: 4px solid #36d1dc;
}

.contact-info p {
    margin: 8px 0;
}

@media screen and (max-width: 768px) {
    .hero-section {
        padding: 24px 0;
    }

    .section-header {
        flex-direction: column;
        text-align: center;
    }

    .section-icon {
        margin: 0 auto;
    }
}
</style>